import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextareaAutosize
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import { getDomainIdSync } from 'utils/utils';
import ReactQuill from 'react-quill';
import "react-quill/dist/quill.snow.css";
import { useSystemTheme } from "context/SystemThemeContext";

const AddAnswer = ({ apiEndpoint, id, onSuccess }) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const openDialog = () => {
    setIsDialogOpen(true);
    setEditedText('');
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditedText('');
    setIsConfirmationOpen(false);
  };

  const openConfirmationDialog = () => {
    if (editedText.trim()) setIsConfirmationOpen(true);
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationOpen(false);
  };

  const handleSave = async () => {
    try {
      const domainId = getDomainIdSync();
      const response = await axios.patch(`${LAMBDA_BASE_URL}/edit-answer/${id}`, {
        id,
        answer: editedText,
      }, {
        headers: {
          'DOMAIN-TOKEN': domainId
        }
      });

      if (response.status === 200) {
        if (onSuccess) onSuccess();
        closeDialog();
      }
    } catch (error) {
      console.error("Error saving the answer:", error.response?.data || error.message);
    }
  };

  return (
    <>
      <Button
        onClick={openDialog}
        style={{
          background: '#219297',
          color: 'white',
          border: 'none',
          fontWeight: 500,
          fontSize: '10px',
          lineHeight: '12px',
          borderRadius: '4px',
          padding: '8px'
        }}
      >
        Add Answer
      </Button>

      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
            transition: "background-color 0.3s ease",
          },
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? "#e2e8f0" : undefined,
          fontWeight: 600,
          borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
        }}>
          Add Answer
        </DialogTitle>
        
        <DialogContent dividers sx={{ 
          padding: "32px",
          borderColor: isDarkMode ? "#2d2d3f" : "#e0e0e0",
        }}>
          <ReactQuill
            theme="snow"
            value={editedText}
            onChange={setEditedText}
            style={{
              minHeight: "200px",
            }}
            placeholder="Enter your answer..."
            className={isDarkMode ? "dark-mode-quill" : ""}
          />
        </DialogContent>
        
        <DialogActions sx={{
          padding: "16px 24px",
          borderTop: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
        }}>
          <Button
            onClick={openConfirmationDialog}
            variant="contained"
            sx={{
              background: "#219297",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { background: "#1a767a" }
            }}
          >
            Save
          </Button>
          <Button 
            onClick={closeDialog}
            sx={{ 
              color: isDarkMode ? "#a0aec0" : "#219297",
              textTransform: "none",
              "&:hover": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "rgba(33,146,151,0.04)",
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={isConfirmationOpen} 
        onClose={closeConfirmationDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
            transition: "background-color 0.3s ease",
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? "#e2e8f0" : undefined,
          fontWeight: 600,
        }}>
          Confirm Save
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "16px" }}>
          <p style={{ color: isDarkMode ? "#e2e8f0" : "#333" }}>
            Are you sure you want to save this answer?
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={closeConfirmationDialog}
            sx={{ 
              color: isDarkMode ? "#a0aec0" : "#219297",
              textTransform: "none",
              "&:hover": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "rgba(33,146,151,0.04)",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: "#219297",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { background: "#1a767a" },
            }}
          >
            Yes, Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add global styles for Quill editor dark mode */}
      <style>
        {`
          .dark-mode-quill .ql-toolbar.ql-snow {
            background-color: ${isDarkMode ? '#2d2d3f' : '#fff'} !important;
            border-color: ${isDarkMode ? '#4a5568' : '#ccc'} !important;
          }
          .dark-mode-quill .ql-container.ql-snow {
            background-color: ${isDarkMode ? '#060C18' : '#fff'} !important;
            border-color: ${isDarkMode ? '#4a5568' : '#ccc'} !important;
          }
          .dark-mode-quill .ql-editor {
            color: ${isDarkMode ? '#e2e8f0' : '#333'} !important;
            background-color: ${isDarkMode ? '#060C18' : '#fff'} !important;
          }
          .dark-mode-quill .ql-editor.ql-blank::before {
            color: ${isDarkMode ? '#a0aec0' : '#999'} !important;
          }
          .dark-mode-quill .ql-stroke {
            stroke: ${isDarkMode ? '#e2e8f0' : '#333'} !important;
          }
          .dark-mode-quill .ql-fill {
            fill: ${isDarkMode ? '#e2e8f0' : '#333'} !important;
          }
          .dark-mode-quill .ql-picker-label {
            color: ${isDarkMode ? '#e2e8f0' : '#333'} !important;
          }
          .dark-mode-quill .ql-picker-options {
            background-color: ${isDarkMode ? '#2d2d3f' : '#fff'} !important;
            border-color: ${isDarkMode ? '#4a5568' : '#ccc'} !important;
          }
          .dark-mode-quill .ql-picker-item {
            color: ${isDarkMode ? '#e2e8f0' : '#333'} !important;
          }
          .dark-mode-quill .ql-picker-item:hover {
            color: #219297 !important;
          }
        `}
      </style>
    </>
  );
};

AddAnswer.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  apiEndpoint: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default AddAnswer;